import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ICONS } from "../../../utils/fontawesome";
import planningApi from "../../../api/planningApi";

const toDateTimeInput = (value) => {
  if (!value) return "";
  const dateObj = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(dateObj.getTime())) return "";
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const BlocageCreateCard = ({
  isOpen,
  onToggle,
  formValues,
  setFormValues,
  isFullClosure,
  setIsFullClosure,
  ateliers = [],
  zones = [],
  onCreated,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const selectedAteliers = Array.isArray(formValues.selectedAteliers)
    ? formValues.selectedAteliers
    : [];
  const selectedZones = Array.isArray(formValues.selectedZones)
    ? formValues.selectedZones
    : [];

  const resolveOptionId = (item, fallbackIndex, prefix) => {
    const rawId =
      item?.id ??
      item?.atelier_id ??
      item?.zone_id ??
      item?.value ??
      item?.pk;
    if (rawId === undefined || rawId === null || rawId === "") {
      return `${prefix}-${fallbackIndex}`;
    }
    return String(rawId);
  };

  const atelierOptions = (Array.isArray(ateliers) ? ateliers : []).map((a, index) => ({
    id: resolveOptionId(a, index, "atelier"),
    label: a.nom || a.label || "Atelier",
  }));

  const zoneOptions = (Array.isArray(zones) ? zones : []).map((z, index) => ({
    id: resolveOptionId(z, index, "zone"),
    label: z.nom || z.label || "Zone",
    actif: z.actif,
  }));
  const toggleSelection = (field, value) => {
    setFormValues((prev) => {
      const current = Array.isArray(prev[field]) ? prev[field] : [];
      const exists = current.includes(value);
      return {
        ...prev,
        [field]: exists ? current.filter((v) => v !== value) : [...current, value],
      };
    });
  };

  const handleSubmit = async () => {
    setSubmitError("");
    setIsSubmitting(true);
    try {
      const normalizeIds = (list = []) =>
        (Array.isArray(list) ? list : [list]).map((value) => {
          if (typeof value === "string" && /^\d+$/.test(value)) return Number(value);
          return value;
        });

      const payload = {
        date_debut: formValues.date_debut,
        date_fin: formValues.date_fin,
        complet: isFullClosure,
        atelier: isFullClosure ? [] : normalizeIds(selectedAteliers),
        zone: isFullClosure ? [] : normalizeIds(selectedZones),
      };

      await planningApi.createBlocage(payload);

      setFormValues({
        date_debut: "",
        date_fin: "",
        selectedAteliers: [],
        selectedZones: [],
      });
      setIsFullClosure(false);
      if (typeof onCreated === "function") {
        onCreated();
      }
      if (typeof onToggle === "function" && isOpen) {
        onToggle();
      }
    } catch (error) {
      const apiError =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        "Impossible de créer l'indisponibilité.";
      setSubmitError(apiError);
      console.error("Erreur lors de la création d'un blocage :", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="border border-dashed border-slate-300 rounded-lg px-3 py-3 text-slate-600">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center justify-between w-full text-left hover:text-slate-900 transition-colors"
      >
        <span className="text-sm font-medium">Ajout d'une indisponibilité</span>
        <span className="h-8 w-8 flex items-center justify-center rounded-full border border-slate-300 text-slate-600">
          <FontAwesomeIcon icon={isOpen ? ICONS.close : ICONS.plus} className="h-4 w-4" />
        </span>
      </button>

      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? "max-h-[420px] opacity-100 mt-3" : "max-h-0 opacity-0"
        }`}
      >
        <div className="flex flex-col gap-2 text-sm text-slate-700">
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-wide text-slate-500">
              Date début
            </span>
            <input
              type="datetime-local"
              value={toDateTimeInput(formValues.date_debut) || ""}
              onChange={(e) =>
                setFormValues((prev) => ({ ...prev, date_debut: e.target.value }))
              }
              className="border border-slate-300 rounded-md px-2 py-2 focus:outline-none focus:ring-2 focus:ring-orange-200"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-wide text-slate-500">
              Date fin
            </span>
            <input
              type="datetime-local"
              value={toDateTimeInput(formValues.date_fin) || ""}
              onChange={(e) =>
                setFormValues((prev) => ({ ...prev, date_fin: e.target.value }))
              }
              className="border border-slate-300 rounded-md px-2 py-2 focus:outline-none focus:ring-2 focus:ring-orange-200"
            />
          </label>

          <button
            type="button"
            onClick={() => setIsFullClosure((prev) => !prev)}
            className="flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900 transition-colors"
          >
            <FontAwesomeIcon
              icon={isFullClosure ? ICONS.toggleOn : ICONS.toggleOff}
              className={`h-4 w-4 ${isFullClosure ? "text-emerald-500" : "text-slate-400"}`}
            />
            <span className="font-semibold">Fermeture MOOD</span>
          </button>

          {!isFullClosure && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-wide text-slate-500">
                  Ateliers concernés
                </span>
                <div className="border border-slate-200 rounded-md p-2 max-h-32 overflow-y-auto text-sm text-slate-700 space-y-1">
                  {atelierOptions.length === 0 ? (
                    <span className="text-slate-400 text-xs">Aucun atelier</span>
                  ) : (
                    atelierOptions.map((opt) => (
                      <label
                        key={opt.id}
                        className="flex items-center gap-2 cursor-pointer select-none"
                      >
                        <input
                          type="checkbox"
                          checked={selectedAteliers.includes(opt.id)}
                          onChange={() => toggleSelection("selectedAteliers", opt.id)}
                          className="h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-200"
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-wide text-slate-500">
                  Zones concernées
                </span>
                <div className="border border-slate-200 rounded-md p-2 max-h-32 overflow-y-auto text-sm text-slate-700 space-y-1">
                  {zoneOptions.length === 0 ? (
                    <span className="text-slate-400 text-xs">Aucune zone</span>
                  ) : (
                    zoneOptions.filter((zone)=> zone.actif === true).map((opt) => (
                      <label
                        key={opt.id}
                        className="flex items-center gap-2 cursor-pointer select-none"
                      >
                        <input
                          type="checkbox"
                          checked={selectedZones.includes(opt.id)}
                          onChange={() => toggleSelection("selectedZones", opt.id)}
                          className="h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-200"
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
          <div>
            <button
              type="submit"
              className="mt-2 w-full bg-orange-600 text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-orange-700 transition-colors"
              onClick={handleSubmit}
              disabled={isSubmitting}
          >
            {isSubmitting ? "Création..." : "Créer l'indisponibilité"}
          </button>
          {submitError && (
            <p className="mt-1 text-xs text-red-600">{submitError}</p>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlocageCreateCard;
